import type { LocalDate } from '@cadencia/contracts';
import { startOfLocalWeek, weekdayIndex } from '../units/local-date.js';

/**
 * Cuando avisar (§8 de UI_UX_SPEC.md).
 *
 * Son TRES avisos y no habra mas: recordatorio de entrenamiento, final del
 * descanso y check-in semanal. El §8 lo cierra con un «nada mas» y un unico
 * interruptor global, sin configuracion granular en V1.
 *
 * POR QUE ESTO ES DOMINIO Y NO PANTALLA
 *
 * «Avisar» es una accion de plataforma, pero **cuando avisar es una regla**: si
 * hay sesion pendiente, si el check-in de esta semana ya se hizo, a que hora
 * suele entrenar esta persona. Escrito en la pantalla, esa regla acabaria
 * duplicada en Android y en Windows y las dos se separarian. Aqui no hay reloj
 * propio (ADR-007): el instante entra por parametro.
 *
 * EL AVISO DEL DESCANSO NO ESTA AQUI, Y ES A PROPOSITO
 *
 * No se programa: se dispara cuando el temporizador llega a cero, con la
 * aplicacion delante. No hay nada que decidir, asi que no hay regla que
 * escribir. Lo unico que se consulta es si el usuario lo quiere encendido.
 */

/** Hora del dia, 0-23. */
export type Hour = number;

/**
 * La hora a la que se avisa cuando todavia no se sabe a que hora entrena.
 *
 * Las 18:00 no es un capricho ni un promedio de nadie: es la unica franja que
 * no molesta por ningun extremo —ni de madrugada ni en mitad de la jornada— y
 * solo se usa hasta que haya historial de verdad. En cuanto lo hay, manda el
 * historial.
 */
export const DEFAULT_REMINDER_HOUR: Hour = 18;

/**
 * Sesiones completadas que hacen falta para fiarse del historial.
 *
 * Con una sola sesion, «la franja habitual» es la hora a la que entrenaste una
 * vez, que puede ser un sabado raro. Tres ya dibujan una costumbre, y es un
 * numero que se alcanza en la primera semana de un bloque de cuatro sesiones.
 */
export const MIN_SESSIONS_FOR_HABIT = 3;

export interface UsualHourInput {
  /** Horas locales (0-23) a las que empezaron las sesiones ya hechas. */
  startedHours: readonly Hour[];
}

/**
 * La franja habitual del usuario: la MEDIANA de las horas a las que ha
 * empezado a entrenar.
 *
 * Mediana y no media. Quien entrena a las 19:00 entre semana y una vez empezo a
 * las 9:00 un domingo tiene una media de las 17:00, que es una hora a la que no
 * ha entrenado nunca. La mediana ignora ese domingo, que es lo que se quiere:
 * la costumbre, no el promedio.
 */
export function usualTrainingHour(input: UsualHourInput): Hour {
  const horas = [...input.startedHours].filter(
    (hora) => Number.isInteger(hora) && hora >= 0 && hora <= 23,
  );
  if (horas.length < MIN_SESSIONS_FOR_HABIT) return DEFAULT_REMINDER_HOUR;

  horas.sort((a, b) => a - b);
  const medio = Math.floor(horas.length / 2);
  if (horas.length % 2 === 1) return horas[medio] as Hour;
  // Par: se baja al menor de los dos centrales. Avisar antes es recuperable
  // —te ha avisado y todavia no ibas—; avisar tarde no.
  return horas[medio - 1] as Hour;
}

export interface WorkoutReminderInput {
  /** Hay una sesion HOY planificada y sin empezar. */
  hasPendingSessionToday: boolean;
  /** Hora local actual, 0-23. */
  currentHour: Hour;
  /** La franja habitual, de `usualTrainingHour`. */
  usualHour: Hour;
  /** Ya se aviso hoy: el aviso es UNO por dia, no un recordatorio insistente. */
  alreadyNotifiedToday: boolean;
}

export interface ReminderDecision {
  due: boolean;
  reasonCodes: string[];
}

/**
 * ¿Toca el recordatorio de entrenamiento?
 *
 * El §8 lo condiciona a que haya «sesion planificada y no iniciada». Se añade
 * lo que el §8 da por supuesto y conviene escribir: **una vez al dia**. Un
 * recordatorio que se repite es la forma mas rapida de que se apaguen todos.
 *
 * Se avisa DESDE la hora habitual en adelante, no solo en punto: si a las 18:00
 * el movil estaba apagado, a las 19:00 el aviso sigue teniendo sentido —la
 * sesion sigue sin hacerse—. Lo que no se hace es avisar antes de tiempo.
 */
export function workoutReminderDue(input: WorkoutReminderInput): ReminderDecision {
  const reasonCodes: string[] = [];

  if (!input.hasPendingSessionToday) reasonCodes.push('no_pending_session');
  if (input.alreadyNotifiedToday) reasonCodes.push('already_notified_today');
  if (input.currentHour < input.usualHour) reasonCodes.push('before_usual_hour');

  return { due: reasonCodes.length === 0, reasonCodes };
}

export interface WeeklyCheckinInput {
  today: LocalDate;
  /** Hora local actual, 0-23. */
  currentHour: Hour;
  /** El check-in de la semana de `today` ya esta hecho. */
  doneThisWeek: boolean;
  alreadyNotifiedToday: boolean;
}

/**
 * A partir de que hora del lunes cuenta como «por la mañana».
 *
 * El §8 dice «lunes por la mañana» y no una hora. Las 9:00 es tarde para
 * despertar a nadie y pronto para que el lunes se haya ido entero.
 */
export const CHECKIN_HOUR: Hour = 9;

/**
 * ¿Toca avisar de que el check-in semanal esta disponible?
 *
 * Solo el LUNES, y solo si no se ha hecho ya el de esa semana. El check-in mira
 * la semana que termina, asi que avisar el martes de uno que se hizo el lunes
 * seria avisar de algo que ya no existe.
 */
export function weeklyCheckinDue(input: WeeklyCheckinInput): ReminderDecision {
  const reasonCodes: string[] = [];

  if (weekdayIndex(input.today) !== 0) reasonCodes.push('not_monday');
  if (input.doneThisWeek) reasonCodes.push('already_done');
  if (input.alreadyNotifiedToday) reasonCodes.push('already_notified_today');
  if (input.currentHour < CHECKIN_HOUR) reasonCodes.push('too_early');

  return { due: reasonCodes.length === 0, reasonCodes };
}

/** El lunes de la semana a la que pertenece `today`. Para saber de que check-in se habla. */
export function checkinWeekOf(today: LocalDate): LocalDate {
  return startOfLocalWeek(today);
}
